// ============================================
// src/components/home/VideoShowcase.tsx
// ============================================
import { Container, Box, Typography, Grid } from '@mui/material';
import { Videocam, Code } from '@mui/icons-material';

interface VideoItem {
  src: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

export default function VideoShowcase() {
  const videos: VideoItem[] = [
    {
      src: '/vid/dron-rio-optimized.webm',
      title: 'Monitoreo Aéreo',
      description: 'Captura de datos mediante drones inteligentes para análisis ambiental y geoespacial.',
      icon: <Videocam sx={{ fontSize: 40 }} />,
    },
    {
      src: '/vid/programador-motion-optimized.webm',
      title: 'Desarrollo Continuo',
      description: 'Plataforma en constante evolución con las últimas tecnologías en IA y machine learning.',
      icon: <Code sx={{ fontSize: 40 }} />,
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Gradiente decorativo superior */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '200px',
          background: 'linear-gradient(to bottom, rgba(30, 64, 175, 0.05), transparent)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg">
        {/* Encabezado de la sección */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 700,
              fontSize: '0.875rem',
              letterSpacing: 1.5,
            }}
          >
            Tecnología Avanzada
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mt: 1,
              mb: 2,
              color: 'text.primary',
            }}
          >
            Cómo Funciona Nuestra{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(45deg, #1e40af 30%, #3b82f6 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Plataforma
            </Box>
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 700, mx: 'auto', lineHeight: 1.6 }}
          >
            Combinamos inteligencia artificial con tecnología de vanguardia para ofrecer 
            soluciones de monitoreo sin precedentes
          </Typography>
        </Box>

        {/* Grid de videos */}
        <Grid container spacing={4}>
          {videos.map((video, index) => (
            <Grid key={index} size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 300, md: 400 },
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 30px 60px rgba(30, 64, 175, 0.2)',
                  },
                  '&:hover video': {
                    filter: 'blur(0px)',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                {/* Video de fondo */}
                <Box
                  component="video"
                  autoPlay
                  loop
                  muted
                  playsInline
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'blur(2px) brightness(0.7)',
                    transform: 'scale(1)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <source src={video.src} type="video/webm" />
                </Box>

                {/* Overlay con gradiente */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 60%)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    p: 4,
                  }}
                >
                  {/* Icono */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      color: 'white',
                      mb: 2,
                      boxShadow: '0 4px 14px 0 rgba(30, 64, 175, 0.4)',
                    }}
                  >
                    {video.icon}
                  </Box>

                  {/* Título */}
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      mb: 1,
                    }}
                  >
                    {video.title}
                  </Typography>

                  {/* Descripción */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      lineHeight: 1.6,
                    }}
                  >
                    {video.description}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}